import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  onMount,
  onCleanup,
  ErrorBoundary,
  Show,
} from 'solid-js'
import { AllChat, characterStore, chatStore, toastStore, userStore } from '/web/store'
import { warmupManager } from '/web/shared/aifans/WarmupManager'
import { AppSchema } from '/common/types'
import { A, useNavigate, useParams } from '@solidjs/router'
import { getAssetUrl, setComponentPageTitle } from '/web/shared/util'
import { isLoggedIn } from '/web/store/api'

import clockIcon from '/web/asset/aifans/model-clock-icon.png'
import locationIcon from '/web/asset/aifans/model-location-icon.png'
import GradientButton from '/web/shared/aifans/GradientButton'
import ModelTraits from '../../shared/aifans/model/ModelTraits'
import ModelPhotos from '/web/shared/aifans/model/ModelPhotos'
import LiveChatButton from '/web/shared/aifans/model/buttons/LiveChatButton'
import GenerateButton from '/web/shared/aifans/model/buttons/GenerateButton'
import Footer from '/web/shared/aifans/Footer'
import Header from '/web/shared/aifans/Header'
import { ChatCharacter } from '../Character/util'
import { getModelGallery } from '/web/store/data/gallery'
import { ModelGalleryImage } from '/common/types/aifans-schema'
import { NoTitleModal } from '/web/shared/Modal'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { useInfiniteScroll } from '/web/shared/hooks/useInfiniteScroll'
import { events, EVENTS } from '/web/emitter'
import { abbreviateNumber, getBaseLikes, getModelUrl } from '/web/shared/aifans/lib/model-util'
import ModelPageSignupModal from './ModelPageSignupModal'
import { getOrCreateChatWithCharacter } from '/web/shared/aifans/lib/chat-util'
import { Link, Meta, Title } from '@solidjs/meta'
import { getCanonicalUrl, getWebBaseUrl } from '/web/shared/aifans/lib/config-util'

const ModelPage: Component = () => {
  const params = useParams()
  const nav = useNavigate()

  const [character, setCharacter] = createSignal<AppSchema.Character | undefined>(undefined)
  const [notFound, setNotFound] = createSignal(false)

  const subscribedToModel = userStore().aifansSubscriptions?.find(
    (subscription) =>
      subscription.characterId === character()?._id && subscription.status === 'active'
  )

  const [avatarUrl, setAvatarUrl] = createSignal<string | undefined>(undefined)
  const [vipFeaturedImageUrl, setVipFeaturedImageUrl] = createSignal<string | undefined>(undefined)
  const [country, setCountry] = createSignal<string | undefined>(undefined)
  const [liveChatId, setLiveChatId] = createSignal<string | undefined>(undefined)
  const [modelGalleryImages, setModelGalleryImages] = createSignal<ModelGalleryImage[]>([])
  const [modalImage, setModalImage] = createSignal<string | undefined>(undefined)
  const [isSubscribed, setIsSubscribed] = createSignal<boolean>(false)
  const [subscribing, setSubscribing] = createSignal<boolean>(!!subscribedToModel)
  const [showSubscribeModal, setShowSubscribeModal] = createSignal<boolean>(false)
  const [liking, setLiking] = createSignal(false)
  const [isLiked, setIsLiked] = createSignal(false)
  const [likeCount, setLikeCount] = createSignal('')

  const [hasMore, setHasMore] = createSignal(true)
  const [page, setPage] = createSignal(1)
  const pageSize = 15 // Adjust this value as needed

  const chars = characterStore((s) => {
    return {
      list: s.characters.list,
      map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
        (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
        {}
      ),
      loaded: s.characters.loaded,
    }
  })

  const state = chatStore((s) => ({
    chats: s.allChats.map((chat) => ({
      _id: chat._id,
      name: chat.name,
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
      characterId: chat.characterId,
      characters: toChatListState(s.allChars.map, chat),
      messageCount: chat.messageCount,
    })),
    chars: s.allChars.map,
    allChats: s.allChats,
  }))

  const [paymentUrl, setPaymentUrl] = createSignal<string>()

  events.on(EVENTS.charUpdated, (char: AppSchema.Character, action: 'created' | 'updated') => {
    const baseLikes = getBaseLikes(char?._id)

    const likes = char?.likes ? char.likes + baseLikes : baseLikes
    setLikeCount(abbreviateNumber(likes))
  })

  onMount(() => {
    if (!chars.loaded) {
      characterStore.getCharacters(true)
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'payment-success' || event.data.type === 'subscription-success') {
        const message =
          event.data.type === 'subscription-success'
            ? 'Successfully subscribed!'
            : 'Payment successful!'
        toastStore.success(message)
        setShowSubscribeModal(false)
        userStore.loadAIFansSubscriptions()
        userStore.getProfile()
      }
    }

    window.addEventListener('message', handleMessage)
    onCleanup(() => {
      window.removeEventListener('message', handleMessage)
      warmupManager.cleanup()
    })
  })

  createEffect(() => {
    // Wait for characters to load before checking
    if (!chars.loaded) {
      return
    }

    // Find character by ID or slug, ensuring it matches the category slug if provided
    const char = chars.list.find(
      (c) =>
        (c._id === params.id || c.slug === params.id) &&
        (!params.categorySlug || c.categorySlug === params.categorySlug)
    )

    if (char) {
      setCharacter(char)
      setNotFound(false)

      // Fire off throttled warm up request when character changes
      warmupManager.triggerWarmUp()
    } else {
      setNotFound(true)
      return
    }

    if (!!character()) {
      if (char?.country) {
        let regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
        setCountry(regionNames.of(char?.country))
      }
    }

    const avatar = character()?.avatar
    if (avatar) {
      setAvatarUrl(getAssetUrl(avatar, true))
    }

    const vipFeaturedImage = character()?.vipFeaturedImage
    if (vipFeaturedImage) {
      setVipFeaturedImageUrl(getAssetUrl(vipFeaturedImage, true))
    }

    if (char?._id) {
      const baseLikes = getBaseLikes(char?._id)

      const likes = char?.likes ? char.likes + baseLikes : baseLikes
      setLikeCount(abbreviateNumber(likes))

      loadModelGallery(1)
    }
  })

  const chats = createMemo(() => {
    const filterCharId = character()?._id

    return state.chats.filter((chat) => {
      if (filterCharId && !chat.characters.some((c) => c._id === filterCharId)) return false

      return true
    })
  })

  createEffect(() => {
    const latestChat = chats().sort(function (a, b) {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    })

    if (latestChat && latestChat[0]) {
      setLiveChatId(latestChat[0]._id)
    }

    userStore.loadAIFansSubscriptions()
  })

  createEffect(() => {
    let subscribedToModel = userStore().aifansSubscriptions?.find(
      (subscription) =>
        subscription.characterId === character()?._id && subscription.status === 'active'
    )

    const isSupport = userStore().user?.role === 'support'

    setIsSubscribed(!!subscribedToModel || isSupport)

    const charId = character()?._id
    const isLiked = charId ? userStore.getState().profile?.likedCharacters?.includes(charId) : false
    setIsLiked(!!isLiked)
  })

  const onClickToChat = () => {
    if (isLoggedIn()) {
      // if (!isSubscribed()) {
      //   setShowSubscribeModal(true)
      //   return
      // }

      if (character()?._id) {
        getOrCreateChatWithCharacter(character()?._id!, (url) => nav(url))
      }
    } else {
      userStore.loginModal(true)
    }
  }

  const onClickLike = async () => {
    if (liking()) return

    if (!isLoggedIn()) {
      userStore.loginModal(true)
    } else {
      setLiking(true)
      if (!isLiked()) {
        await userStore.likeCharacter(character()?._id!)
      } else {
        await userStore.unlikeCharacter(character()?._id!)
      }
      setLiking(false)
    }
  }

  const onClickToGenerate = () => {
    if (isLoggedIn()) {
      nav(`/generate-image/${character()?._id}`)
    } else {
      userStore.loginModal(true)
    }
  }

  const loadModelGallery = async (pageNumber: number) => {
    const res = await getModelGallery(character()?._id!, pageNumber, pageSize)
    if (res?.result?.images) {
      if (pageNumber === 1) {
        setModelGalleryImages(res.result.images)
      } else {
        setModelGalleryImages((prev) => [...prev, ...res.result!.images])
      }
      setHasMore(res.result.images.length === pageSize)
    }
  }

  const loadMore = () => {
    if (hasMore()) {
      const nextPage = page() + 1
      setPage(nextPage)
      loadModelGallery(nextPage)
    }
  }

  const { setTarget } = useInfiniteScroll(loadMore)

  const showTokensModal = () => {
    setShowSubscribeModal(false)

    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return
    }

    userStore.buyTokensModal(true)
  }

  const subscribeToModel = async () => {
    if (!isLoggedIn()) {
      setShowSubscribeModal(false)
      userStore.loginModal(true)
      return
    }

    try {
      setSubscribing(true)
      const response = await aifansSubscriptionApi.createAIFansSubscription(character()?._id!)

      if (response.result?.redirectURL) {
        setPaymentUrl(response.result.redirectURL)
      }
    } catch (error) {
      setSubscribing(false)
      setShowSubscribeModal(false)
      toastStore.error('There was a problem subscribing to this model. Please try again.')
    }
  }

  const defaultDescription = `Hi I'm Candice Nice, a vibrant influencer who loves to please my followers with a
                cheerful and playful personality. I just love to dress as Harley Quinn and bring her
                to life through cosplay. From intimate moments to exotic adventures, my passion for
                cosplay has no boundaries.`

  const closeSubscribeModal = () => {
    setShowSubscribeModal(false)
    setSubscribing(false)
    setPaymentUrl(undefined)
    userStore.loadAIFansSubscriptions()
    userStore.getProfile()
  }

  return (
    <Show
      when={!notFound()}
      fallback={
        <div class="h-full w-full bg-black">
          <div class="absolute left-3 right-3 top-0 z-50 md:relative md:left-auto md:right-auto md:top-auto md:bg-white/5">
            <div class="xl:container md:mx-auto md:px-5 lg:px-[20px] xl:px-[40px] 2xl:max-w-[1775px] 2xl:px-[60px]">
              <Header />
            </div>
          </div>
          <div class="flex h-screen items-center justify-center">
            <div class="text-center">
              <h1 class="mb-4 font-clash text-4xl text-white">404 - Model Not Found</h1>
              <A href="/" class="text-pink-500 hover:text-pink-400">
                Return Home
              </A>
            </div>
          </div>
          <Footer />
        </div>
      }
    >
      {character()?.name && (
        <Title>{`CosplayFans - ${character()?.realName} cosplays as ${character()?.name}`}</Title>
      )}
      <Link
        rel="canonical"
        href={getCanonicalUrl(getModelUrl(character()?.categorySlug, character()?.slug))}
      />
      {character()?.name && (
        <Meta
          name="og:title"
          content={`CosplayFans - ${character()?.realName} cosplays as ${character()?.name}`}
        />
      )}
      {character()?.metaDescription && (
        <>
          <Meta name="og:description" content={character()?.metaDescription} />
          <Meta name="description" content={character()?.metaDescription} />
        </>
      )}
      {avatarUrl() && <Meta name="og:image" content={avatarUrl()} />}
      <div class="h-full w-full bg-black">
        <div class=" w-full bg-model-backdrop bg-[center_top]">
          <div class="relative w-full">
            <div class="absolute left-3 right-3 top-0 z-50 md:relative md:left-auto md:right-auto md:top-auto md:bg-white/5 ">
              <div class="xl:container md:mx-auto md:px-5 lg:px-[20px] xl:px-[40px] 2xl:max-w-[1775px] 2xl:px-[60px]  ">
                <Header />
              </div>
            </div>
            <div class="w-full xl:container md:mx-auto md:px-5 md:py-[27px] lg:px-[20px] xl:px-[40px] 2xl:max-w-[1775px] 2xl:px-[60px]">
              <div class="md:flex">
                <div class="relative h-[445px] w-full overflow-hidden md:shrink-0 md:grow-0 md:basis-[387px] md:rounded-[12px]">
                  <img
                    class="absolute h-full w-full object-cover"
                    src={avatarUrl()}
                    alt={`${character()?.realName} cosplays as ${character()?.name}`}
                  />
                </div>
                <div class="md:ml-6">
                  <div class="mx-[22px] my-[14px] pr-8 text-left md:my-0">
                    <button onClick={onClickLike} class="relative">
                      <span class="relative">
                        <h1 class="inline font-clash text-3xl leading-[40px] text-white md:text-[40px]">
                          {character()?.name}
                        </h1>

                        <img
                          src={
                            isLiked()
                              ? '/images/icons/model-favorite-star-filled.png'
                              : '/images/icons/model-favorite-star.png'
                          }
                          class="absolute right-[-56px] top-[-16px] h-[20px] w-[20px] md:right-[-36px]"
                        />
                      </span>
                    </button>
                    <div class="flex items-center">
                      <Show when={character()?.realName}>
                        <div class="mt-1 font-clash text-base text-gray-400">
                          {character()?.realName}
                        </div>
                      </Show>
                      <div class="ml-2 flex items-center">
                        <img src={locationIcon} class="h-[32px] w-[32px]" />
                        <span class="ml-2 font-clash text-[16px] text-gray-400">{country()}</span>
                      </div>
                    </div>
                  </div>
                  {/*
                  <div class="flex">
                    <div class="ml-[22px] flex items-center">
                      <img src={clockIcon} class="h-[44px] w-[44px]" />
                      <span class="ml-2 font-clash text-[16px]">30 MIN AGO</span>
                    </div>
                  </div>
                    */}
                  <div class="mx-[22px] mb-[30px] mt-[18px]">
                    <div class="md:flex md:flex-col-reverse">
                      <p class="font-clash text-[18px] 2xl:max-w-2xl">
                        {character()?.description || defaultDescription}
                      </p>
                      <div class="mt-[30px] flex flex-col gap-3 md:mb-4 md:mt-0 lg:flex-row lg:gap-5">
                        <div>
                          <LiveChatButton onClick={onClickToChat} />
                        </div>
                        <div>
                          <GradientButton
                            title={`${likeCount() ? 'LIKES ' + likeCount() : 'LIKE'}`}
                            colorStart="#E12F8F"
                            colorEnd="#E12F42"
                            image={
                              isLiked()
                                ? '/images/heart-button-icon.png'
                                : '/images/heart-button-icon-empty.png'
                            }
                            onClick={onClickLike}
                          />
                        </div>
                        <div>
                          <GenerateButton onClick={onClickToGenerate} />
                        </div>
                      </div>
                    </div>
                    <ModelTraits
                      personality={character()?.personality}
                      occupation={character()?.occupation}
                      hobbies={character()?.hobbies}
                      relationship={character()?.relationship}
                      bodyType={character()?.bodyType}
                      age={character()?.age}
                      butt={character()?.butt}
                      hair={character()?.hair}
                      breasts={character()?.breasts}
                      ethnicity={character()?.ethnicity}
                    />
                  </div>
                </div>
              </div>
              <div class="mx-2">
                <ModelPhotos
                  locked={!isLoggedIn() || !isSubscribed()}
                  images={modelGalleryImages()}
                  onImage={(url) => setModalImage(url)}
                  onSubscribe={() => setShowSubscribeModal(true)}
                  subscribing={subscribing()}
                  hasMore={hasMore()}
                  setTarget={setTarget}
                  modelName={character()?.name}
                  characterId={character()?._id!}
                />
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <NoTitleModal show={!!modalImage()} close={() => setModalImage()} maxWidth="half">
          <div class="flex justify-center p-4">
            <img class="rounded-md" src={getAssetUrl(modalImage() || '', true)} />
          </div>
        </NoTitleModal>
        <ModelPageSignupModal
          show={showSubscribeModal()}
          subscribeToModel={subscribeToModel}
          showTokensModal={showTokensModal}
          setShowSubscribeModal={closeSubscribeModal}
          avatarUrl={vipFeaturedImageUrl() || avatarUrl()}
          subscribing={subscribing()}
          paymentUrl={paymentUrl()}
        />
      </div>
    </Show>
  )
}

function toCharacterIds(characters?: Record<string, boolean>) {
  if (!characters) return []

  const ids: string[] = []
  for (const [id, enabled] of Object.entries(characters)) {
    if (enabled) ids.push(id)
  }
  return ids
}

function toChatListState(chars: Record<string, AppSchema.Character>, chat: AllChat) {
  const charIds = [chat.characterId].concat(toCharacterIds(chat.characters))

  const seen = new Set<string>()
  const rows: ChatCharacter[] = []
  for (const id of charIds) {
    if (seen.has(id)) continue
    seen.add(id)
    const char = chars[id]
    if (!char) {
      rows.push({ _id: '', name: 'Unknown', description: '', avatar: '' })
      continue
    }

    rows.push({
      _id: char._id,
      name: char.name,
      description: char.description || '',
      avatar: char.avatar,
    })
  }

  return rows
}

export default ModelPage
